/* global JSX */
import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { ModifyUser } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as taskService from "../../../models/services/taskService";
import { ISessionState } from "../../../models/session/sessionState";
import { AddButton } from "../../framework/Button/AddButton";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { ListSearchContainer } from "source/components/framework/List/ListSearchContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { ITaskListItem } from "source/models/task";
import { TaskListStateCol } from "./TaskListStateCol";
import { TaskListHandlersCol } from "./TaskListHandlersCol";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { ListDetailsButton } from "source/components/framework/List/ListDetailsButton";
import { PageListActionContainer } from "source/components/framework/List/PageListActionContainer";
import { TaskEditDialog } from "../TaskEditDialog";
import { TrashBinButton } from "source/components/framework/Button/TrashBinButton";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { RestoreFromTrashBinButton } from "source/components/framework/Button/RestoreFromTrashBinButton/RestoreFromTrashBinButton";
import { EmptyTrashBinButton } from "source/components/framework/Button/EmptyTrashBinButton";
import { ConfirmationDialogResult, TaskHandlerType, TaskRepetitionType } from "ui/Scripts/source/models/enums";
import { TaskListDetailsPanel } from "./TaskListDetailsPanel";
import { PreviewToggleButton } from "source/components/framework/Button/PreviewToggleButton";
import { TaskPrintDialog } from "./TaskPrintDialog";
import { getTaskGeneralFilterFromFilterState, getTaskGeneralFilterChange, ITaskGeneralFilter, ITaskPageFilterState } from "../TaskPage/TaskPageUtils";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ITaskListProp extends ITaskPageFilterState {
    sessionState: ISessionState;
    filterName: string;
    companyId: string;
    addRecord?: boolean;
    canCreateTasks: boolean;
    listFilterIsSet: boolean;
    units: IIdTitleNode[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    thrashBin: boolean;
    titleSuffix?: JSX.Element;
    reportTabs?: React.ReactNode;
    showDocumentPreview?: boolean;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onLoadPageData: () => void;
    onOpenFiltersDialog: () => void;
    onOpenTrashBinDialog: () => void;
    onChangeShowDocumentPreview?: (showDocumentPreview: boolean) => void;
}

interface ITaskListSearchFilter extends ITaskGeneralFilter {
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

//This class is specified only for dynamic property filtering
class TaskListSearchFilter implements ITaskListSearchFilter {
    unitIds: string[];
    stateIds: string[];
    priorityIds: string[];
    taskTypeIds: string[];
    handlerUserCodes: string[];
    personInChargeUserCodes: string[];
    taskTypeHeaderFields: string;
    taskTypeFields: string;
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

interface ITaskListState extends IListUsingComponentState<ITaskListItem>, ITaskListSearchFilter {
    // List
    intialized: boolean;
    isLoading: boolean;

    listModeIsLine: boolean;
    checkedIds: string[];
    selectedTaskTypeId: string;

    // Details
    detailsDataId: string;

    // Dialog
    showTaskPrintDialog: boolean;
}

export class TaskList extends React.Component<ITaskListProp, ITaskListState> {
    sendReadReceipts: string[] = [];

    sortHandlers = (items: ITaskListItem[]): ITaskListItem[] => {
        for (const item of items) {
            ModifyUser.sortModifyUsers(item.handlers, { currentUserCode: this.props.sessionState.code });
        }
        return items;
    };

    constructor(props: ITaskListProp) {
        super(props);
        const generalFilterValues = getTaskGeneralFilterFromFilterState(props);
        this.state = {
            ...generalFilterValues,
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: [],
            selectedId: null,
            selectedTaskTypeId: null,
            checkedIds: [],
            // Details
            detailsDataId: "",
            detailsItem: null,
            // Dialog
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            showTaskPrintDialog: false,
        };
    }

    setReadReceipt = (detailsItem: ITaskListItem) => {
        const props = this.props;
        if (!detailsItem || detailsItem.isRead) return;
        const userIsCreator = detailsItem.createUser.code === props.sessionState.code;
        if (userIsCreator || this.sendReadReceipts.indexOf(detailsItem.id) > -1) return;
        const result = AppUtils.callService(() => taskService.sendTaskReadReceipt(props.companyId, detailsItem.id), false, true);
        if (!result) return;
        this.sendReadReceipts.push(detailsItem.id);
        const items = this.state.items.slice(0);
        const item = items.find(i => i.id === detailsItem.id);
        if (!item) return;
        item.isRead = true;
        this.setState({
            items
        });
    };

    searchItems = async (searchFilter: ITaskListSearchFilter, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true, addRecord: boolean = false, newSelectedId: string = null) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const oneTaskType = searchFilter.taskTypeIds.length === 1;
        const result = await AppUtils.callService(() => taskService.getTaskListItems(props.companyId, !refreshList ? searchFilter.pageSize : (searchFilter.page * searchFilter.pageSize), !refreshList ? searchFilter.page : 1,
            props.thrashBin, searchFilter.filter, searchFilter.unitIds, searchFilter.stateIds, searchFilter.priorityIds, searchFilter.taskTypeIds, searchFilter.handlerUserCodes, searchFilter.personInChargeUserCodes,
            oneTaskType ? searchFilter.taskTypeHeaderFields : null, oneTaskType ? searchFilter.taskTypeFields : null,
            searchFilter.sortColumn, searchFilter.sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, newSelectedId ?? this.state.selectedId, this.state.checkedIds, this.sortHandlers(result.items), resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            ...searchFilter,
            intialized: true,
            page: !refreshList ? result.page : searchFilter.page,
            hasMore: result.hasMore,
            items: items.items,
            selectedId,
            selectedTaskTypeId: detailsItem ? detailsItem.typeId : null,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
        }, () => {
            if (!addRecord) return;
            this.handleAdd();
        });
        this.props.onSetSortData(searchFilter.sortColumn, searchFilter.sortOrderIsAsc);
        this.setReadReceipt(detailsItem);
    };

    refreshList = async (setDialogData: boolean = true, newSelectedId: string = null) => {
        await this.searchItems(Base.filterPropertiesByClass(this.state, TaskListSearchFilter), false, true, false, setDialogData, false, newSelectedId);
        if (!setDialogData) return;
        this.setState({ detailsDataId: Base.getGuid() });
    };

    componentDidMount(): void {
        const props = this.props;
        this.searchItems(Base.filterPropertiesByClass(this.state, TaskListSearchFilter), true, false, false, false, !!props.addRecord);
    }

    componentDidUpdate(prevProps: ITaskListProp, prevState: ITaskListState) {
        const props = this.props;
        const state = this.state;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const filterStateChange = getTaskGeneralFilterChange(prevProps, props);
        const prevSortColumn = prevProps.sortColumn;
        const sortColumn = props.sortColumn;
        const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
        const sortOrderIsAsc = props.sortOrderIsAsc;
        if (dataId === prevDataId && companyId === prevCompanyId &&
            filterStateChange.notChanged &&
            prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
        this.searchItems({ ...filterStateChange, pageSize: state.pageSize, page: state.page, filter: state.filter, sortColumn, sortOrderIsAsc }, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems({ ...state, filter }, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems({ ...state, page: !nextPage ? 1 : state.page + 1, sortColumn, sortOrderIsAsc }, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        const detailsItem = this.state.items.find(i => i.id === selectedId);
        this.setState({
            selectedId,
            checkedIds,
            detailsItem,
            selectedTaskTypeId: detailsItem ? detailsItem.typeId : null
        });
        this.setReadReceipt(detailsItem);
    };
    
    handleListModeChange = (listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    // #region Edit dialog
    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleAdd = () => {
        this.setState(List.getShowEditDialogStateChange(this.state, Base.emptyGuid));
    };
    
    handleEditDialogOk = (id: string) => {
        this.refreshList(false, id);
        this.props.onLoadPageData();
    };

    handleEditDialogCancel = () => {
        this.setState({
            showEditDialog: false,
        });
        this.refreshList(false);
        this.props.onLoadPageData();
    };

    setEditDialogItemByIndex = (index: number) => {
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems({ ...this.state, page: this.state.page + 1 }, false, false, true)));
    };
    // #endregion Edit dialog

    printTask = async (id: string, rowAmount: number, selectedRowIds: string[], printRowInformation: boolean) => {
        const fileName = await AppUtils.callService(() => taskService.getTaskPrint(this.props.companyId, id, rowAmount, selectedRowIds, printRowInformation));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
        this.setState({ showTaskPrintDialog: false });
    };

    handlePrint = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const item = this.state.items.find(i => i.id === state.selectedId);
        if (!item) return;
        if (item.taskRepetitionType === TaskRepetitionType.Repeated) {
            this.setState({ showTaskPrintDialog: true });
        } else {
            this.printTask(state.selectedId, 0, [], false);
        }
    };

    handleEmptyTrashBin = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(Translations.EmptyTaskTrashBinConfirmation, false) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => taskService.emptyTrashBin(props.companyId));
            if (!result) return;
            this.refreshList();
        }
    };

    handleRestoreFromTrashBin = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const result = await AppUtils.callService(() => taskService.restoreTaskFromTrashBin(this.props.companyId, state.selectedId));
        if (!result) return;
        this.refreshList(false);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const beforeList = <>
            {!props.thrashBin &&
                <>
                    <PageListActionContainer
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    filterIsSet={props.listFilterIsSet}
                                    printDisabled={!state.selectedId}
                                    onFilter={props.onOpenFiltersDialog}
                                    onPrint={this.handlePrint}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </ListSearchContainer>
                </>
            }
            {props.thrashBin &&
                <ActionContainer
                    leftItems={
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    }
                    rightItems={
                        <>
                            <Box display="flex" sx={{ marginRight: ".5rem" }}>
                                {props.sessionState.isCompanyAdmin &&
                                    <EmptyTrashBinButton onClick={this.handleEmptyTrashBin} disabled={state.items.length < 1} sx={{ marginRight: ".5rem" }}>
                                        {Translations.EmptyTaskTrashBin}
                                    </EmptyTrashBinButton>
                                }
                                <RestoreFromTrashBinButton onClick={this.handleRestoreFromTrashBin} disabled={!state.detailsItem || !state.detailsItem.removable}>
                                    {Translations.RestoreTaskFromTrashBin}
                                </RestoreFromTrashBinButton>
                            </Box>
                            <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                        </>
                    }
                />
            }
        </>;
        const list = <List
            intialized={state.intialized}
            isLoading={state.isLoading}
            hasMore={state.hasMore}
            globalLoadingInUse={true}
            height={!props.thrashBin ? "calc(100vh - 296px)" : "100%"}
            header={state.listModeIsLine
                ? {
                    columns: [
                        { size: 2, column: "unitCode", content: Translations.Unit },
                        { size: 1, column: "modifyDate", content: Translations.ModifyDate, hidden: props.showDocumentPreview },
                        { size: !props.showDocumentPreview ? 1 : 2, column: "typeCode", content: Translations.TaskType },
                        { size: !props.showDocumentPreview ? 2 : 3, column: "stateCode", content: Translations.State },
                        { size: 1, column: "priorityCode", content: Translations.Priority, hidden: props.showDocumentPreview },
                        { size: 3, column: "name", content: Translations.Title },
                        { size: 2, column: "handlers", content: Translations.PersonsInCharge }
                    ]
                }
                : null}
            lines={state.listModeIsLine
                ? state.items.map((item) => {
                    return {
                        columns: [
                            { size: 2, content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.unitCode ? item.unitCode : Translations.Private}</ListLinkValue> },
                            { size: 1, content: Base.timeToDateStr(item.modifyUser.date), hidden: props.showDocumentPreview },
                            { size: !props.showDocumentPreview ? 1 : 2, content: item.typeCode },
                            { size: !props.showDocumentPreview ? 2 : 3, content: <TaskListStateCol item={item} /> },
                            { size: 1, content: item.priorityCode, hidden: props.showDocumentPreview },
                            { size: 3, content: item.name },
                            { size: 2, content: <TaskListHandlersCol handlers={item.handlers.filter(i => i.taskHandlerType === TaskHandlerType.PersonInCharge)} /> }
                        ],
                        id: item.id
                    };
                })
                : null
            }
            cards={!state.listModeIsLine
                ? state.items.map((item) => {
                    return {
                        sm: 4,
                        rows: [
                            {
                                cols: [
                                    {
                                        size: 6,
                                        title: Translations.Code,
                                        value: <div><ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.unitCode}</ListLinkValue></div>
                                    },
                                    { size: 6, title: Translations.ModifyDate, value: Base.timeToDateStr(item.modifyUser.date) }
                                ]
                            },
                            {
                                cols: [
                                    { title: Translations.Title, value: item.name },
                                ]
                            },
                            {
                                cols: [
                                    { size: 6, title: Translations.State, value: <TaskListStateCol item={item} /> },
                                    { size: 6, title: Translations.Unit, value: item.unitCode ? item.unitCode : Translations.Private }
                                ]
                            },
                            {
                                cols: [
                                    { size: 6, title: Translations.TaskType, value: item.typeCode },
                                    { size: 6, title: Translations.Priority, value: item.priorityCode },
                                ]
                            }],
                        id: item.id
                    };
                })
                : null
            }
            lineDetails={state.detailsItem && !props.showDocumentPreview
                ? <Box position="relative">
                    <GridRow>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={6}>
                                    <ListCardField title={Translations.ModifyDate} value={Base.timeToDateStr(state.detailsItem.modifyUser.date)} />
                                </Grid>
                                <Grid item sm={6}>
                                    <ListCardField title={Translations.TaskType} value={state.detailsItem.typeCode} />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Title} value={state.detailsItem.name} />
                                </Grid>
                            </GridRow>
                        </Grid>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={6}>
                                    <ListCardField title={Translations.State} value={state.detailsItem.stateCode} />
                                </Grid>
                                <Grid item sm={6}>
                                    <ListCardField title={Translations.Priority} value={state.detailsItem.priorityCode} />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Code} value={state.detailsItem.code} />
                                </Grid>
                            </GridRow>
                        </Grid>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.PersonsInCharge} value={Base.getStringWithSeparators(state.detailsItem.handlers.filter(i => i.taskHandlerType === TaskHandlerType.PersonInCharge).map(i => i.name), ", ")} />
                                </Grid>
                            </GridRow>
                        </Grid>
                    </GridRow>
                    <ListDetailsButton
                        onClick={() => this.handleOpenLine(state.selectedId)}
                    />
                </Box>
                : null}
            sortColumn={state.sortColumn}
            sortOrderIsAsc={state.sortOrderIsAsc}
            selectedId={state.selectedId}
            checkedIds={state.checkedIds}
            onSetSelectedIds={this.handleSetSelectedIds}
            onSearchLines={this.handleSearchLines}
            onOpenLine={this.handleOpenLine}
        />;

        return (
            <>
                <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 7, display: "flex" }}>
                    {!props.thrashBin &&
                        <PageTitle title={Translations.Tasks + (props.filterName ? " - " + props.filterName : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))} position="absolute">
                            {props.canCreateTasks &&
                                <TrashBinButton onClick={props.onOpenTrashBinDialog}>
                                    {Translations.TrashBin}
                                </TrashBinButton>
                            }
                            {props.canCreateTasks &&
                                <AddButton onClick={this.handleAdd} sx={{ margingLeft: "24px" }}>
                                    {Translations.AddTask}
                                </AddButton>
                            }
                            <PreviewToggleButton value="preview" selected={props.showDocumentPreview} onChange={() => props.onChangeShowDocumentPreview(!props.showDocumentPreview)} style={{ marginLeft: "24px", padding: "7px" }} />
                        </PageTitle>
                    }
                    {(!props.thrashBin
                        ? <MainListContainer sx={{ width: "100%" }}>
                            {beforeList}
                            {list}
                        </MainListContainer>
                        : <VerticalContainer
                            width="100%"
                            before={beforeList}
                            main={list}
                        />
                    )}
                    {(state.showEditDialog && !!state.editDialogId) &&
                        <TaskEditDialog
                            companyId={props.companyId}
                            sessionState={props.sessionState}
                            taskId={state.editDialogId}
                            hasPrevious={state.editDialogHasPrevious}
                            hasNext={state.editDialogHasNext}
                            onOk={this.handleEditDialogOk}
                            onCancel={this.handleEditDialogCancel}
                            onPrint={this.handlePrint}
                            onRefreshList={() => this.refreshList(false)}
                            onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                            onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        />
                    }
                    {state.showTaskPrintDialog &&
                        <TaskPrintDialog
                            companyId={props.companyId}
                            taskId={state.selectedId}
                            taskTypeId={state.selectedTaskTypeId}
                            sessionState={props.sessionState}
                            onOk={(rowAmount, selectedRowIds, printRowInformation) => this.printTask(state.selectedId, rowAmount, selectedRowIds, printRowInformation)}
                            onClose={() => this.setState({ showTaskPrintDialog: false })}
                        />
                    }
                </div>
                {props.showDocumentPreview &&
                    <TaskListDetailsPanel
                        sessionState={props.sessionState}
                        companyId={props.companyId}
                        taskId={state.selectedId}
                        dataId={state.detailsDataId}
                        editDialogVisible={state.showEditDialog}
                        filterTaskTypeFields={state.taskTypeFields}
                        onClose={() => props.onChangeShowDocumentPreview(false)}
                    />
                }
            </>
        );
    }
}